import { useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useLocalization } from '@/hooks/useLocalization';
import {
  accountsApi,
  skipViewingInvestorTagForAccountsQuery,
} from '@/store/accountsApi';
import {
  useCreateUserTagMutation,
  useGetUserTagAccountsQuery,
  useUpdateUserTagMutation,
  type UserTag,
} from '@/store/usersApi';

export interface TagUpsertModalProps {
  open: boolean;
  onClose: () => void;
  existingTagNames: string[];
  tag?: UserTag;
}

export default function TagUpsertModal({
  open,
  onClose,
  existingTagNames,
  tag,
}: TagUpsertModalProps) {
  const { t } = useLocalization();
  const [name, setName] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<number>>(
    new Set(),
  );
  const [nameError, setNameError] = useState('');
  const [accountsError, setAccountsError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: accountsData,
    isLoading: accountsLoading,
    isFetching: accountsFetching,
    isError: accountsListFailed,
    refetch: refetchAccounts,
  } = accountsApi.useGetAccountsQuery(skipViewingInvestorTagForAccountsQuery, {
    skip: !open,
  });
  // Edit mode only: the tag's currently-assigned accounts (entitlement- and
  // visibility-scoped) come from a dedicated endpoint, not from the tag row.
  const {
    data: tagAccountIds,
    isFetching: tagAccountsFetching,
    isError: tagAccountsFailed,
    refetch: refetchTagAccounts,
  } = useGetUserTagAccountsQuery(open && tag ? tag.tagId : skipToken);
  const [createTag, createState] = useCreateUserTagMutation();
  const [updateTag, updateState] = useUpdateUserTagMutation();

  // Reset fields on open. In edit mode the selection is re-seeded from the fetched
  // account ids by the effect below once they arrive.
  useEffect(() => {
    if (!open) {
      return;
    }

    setName(tag?.name ?? '');
    setSelectedAccountIds(new Set());
    setNameError('');
    setAccountsError('');
    setSaveError('');
    setIsSubmitting(false);
  }, [open, tag]);

  useEffect(() => {
    if (!open || !tag || !tagAccountIds) {
      return;
    }
    setSelectedAccountIds(new Set(tagAccountIds));
  }, [open, tag, tagAccountIds]);

  const accounts = accountsData?.Records ?? [];
  const isSaving =
    isSubmitting || createState.isLoading || updateState.isLoading;
  const isEdit = Boolean(tag);
  const isAccountsBusy =
    accountsLoading || accountsFetching || (isEdit && tagAccountsFetching);
  const accountsLoadFailed =
    accountsListFailed || (isEdit && tagAccountsFailed);
  const hasNoAccounts =
    !isAccountsBusy && !accountsLoadFailed && accounts.length === 0;
  const isFormInvalid =
    name.trim().length === 0 ||
    selectedAccountIds.size === 0 ||
    hasNoAccounts ||
    Boolean(nameError) ||
    Boolean(accountsError);

  const handleAccountChange = (accountId: number, checked: boolean) => {
    setSelectedAccountIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (checked) {
        nextIds.add(accountId);
      } else {
        nextIds.delete(accountId);
      }
      return nextIds;
    });
    setAccountsError('');
    setSaveError('');
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(t('required', 'Required'));
      return;
    }

    if (selectedAccountIds.size === 0) {
      setAccountsError(
        t('viewingTag.selectAtLeastOneAccount', 'Select at least one account.'),
      );
      return;
    }

    const normalizedName = trimmedName.toLocaleLowerCase();
    const ownNormalizedName = tag?.name.trim().toLocaleLowerCase();
    const isDuplicate = existingTagNames.some(
      (existingName) =>
        existingName.trim().toLocaleLowerCase() !== ownNormalizedName &&
        existingName.trim().toLocaleLowerCase() === normalizedName,
    );
    if (isDuplicate) {
      setNameError(
        t('viewingTag.duplicateName', 'A tag with this name already exists.'),
      );
      return;
    }

    setNameError('');
    setAccountsError('');
    setSaveError('');
    setIsSubmitting(true);

    const wealthAccountIds = [...selectedAccountIds];
    const result = tag
      ? await updateTag({
          tagId: tag.tagId,
          name: trimmedName,
          wealthAccountIds,
        })
      : await createTag({ name: trimmedName, wealthAccountIds });

    if (result.error !== undefined) {
      if ('status' in result.error && result.error.status === 409) {
        setNameError(
          t('viewingTag.duplicateName', 'A tag with this name already exists.'),
        );
      } else {
        setSaveError(
          t('viewingTag.saveFailed', "We couldn't save the tag. Try again."),
        );
      }
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
    onClose();
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      transitionDuration={200}
      aria-labelledby='tag-upsert-modal-title'
    >
      <Box
        component='form'
        onSubmit={(event) => {
          void handleSave(event);
        }}
        noValidate
      >
        <DialogTitle id='tag-upsert-modal-title'>
          {tag
            ? t('viewingTag.editTag', 'Edit tag')
            : t('viewingTag.createTag', 'Create tag')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2.5, pt: 0.5 }}>
            <TextField
              autoFocus
              required
              fullWidth
              label={t('viewingTag.tagName', 'Tag name')}
              value={name}
              disabled={isSaving}
              error={Boolean(nameError)}
              helperText={
                nameError ? <span role='alert'>{nameError}</span> : undefined
              }
              onChange={(event) => {
                setName(event.target.value);
                setNameError('');
                setSaveError('');
              }}
            />

            <FormControl
              component='fieldset'
              fullWidth
              error={Boolean(accountsError)}
            >
              <FormLabel component='legend'>
                {t('viewingTag.accounts', 'Accounts')}
              </FormLabel>

              {isAccountsBusy ? (
                <Box
                  aria-live='polite'
                  sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                >
                  <CircularProgress
                    size={28}
                    aria-label={t('loading', 'Loading')}
                  />
                </Box>
              ) : accountsLoadFailed ? (
                <Alert
                  severity='error'
                  role='alert'
                  action={
                    <Button
                      color='inherit'
                      size='small'
                      onClick={() => {
                        void refetchAccounts();
                        if (isEdit) {
                          void refetchTagAccounts();
                        }
                      }}
                    >
                      {t('retry', 'Retry')}
                    </Button>
                  }
                >
                  {t(
                    'viewingTag.accountsLoadFailed',
                    "We couldn't load your accounts. Try again.",
                  )}
                </Alert>
              ) : hasNoAccounts ? (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 2,
                    py: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t(
                      'viewingTag.noAccountsAvailable',
                      'No accounts available to assign to this tag.',
                    )}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 260,
                    overflowY: 'auto',
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {accounts.map((account) => (
                    <FormControlLabel
                      key={account.AccountId}
                      disabled={isSaving}
                      sx={{
                        alignItems: 'flex-start',
                        display: 'flex',
                        m: 0,
                        minHeight: 48,
                        width: '100%',
                      }}
                      control={
                        <Checkbox
                          checked={selectedAccountIds.has(account.AccountId)}
                          onChange={(event) => {
                            handleAccountChange(
                              account.AccountId,
                              event.target.checked,
                            );
                          }}
                        />
                      }
                      label={
                        <Box sx={{ py: 0.75 }}>
                          <Typography variant='body2'>
                            {account.Name}
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                          >
                            {account.InstitutionName}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </Box>
              )}

              {accountsError && (
                <FormHelperText role='alert'>{accountsError}</FormHelperText>
              )}
            </FormControl>

            {saveError && (
              <Alert
                severity='error'
                role='alert'
              >
                {saveError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            type='button'
            onClick={handleClose}
            disabled={isSaving}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={
              isSaving || isAccountsBusy || accountsLoadFailed || isFormInvalid
            }
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {t('save', 'Save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
